import React, { Fragment, ReactElement, useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { AppContext } from "AppContext";
import { formatPort, separatePorts } from "helpers/docker";

import { isNil } from "ramda";

import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import ShowProxyInfo from "components/docker/proxy/actions/ShowProxyInfo";
import EmptyList from "components/shared/emptyList/EmptyList";
import IconSelector from "components/shared/images/IconSelector";

import { getClusterProxy } from "redux/handlers/clusterHandler";
import { getServerSites } from "redux/handlers/dockerHandler";

import { getDockerWebsiteNumber } from "redux/selectors/dockerWebsiteSelector";
import { getServerDetails } from "redux/selectors/serversSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { IClusterServiceData } from "types/api/clusterApiInterface";
import { IContainerData } from "types/api/dockerApiInterface";

import CreateProxy from "../actions/proxy/CreateProxy";
import DeleteProxy from "../actions/proxy/DeleteProxy";

const ClusterProxy = ({
 serviceList
}: {
 serviceList: Array<IClusterServiceData>;
}): ReactElement => {
 const dispatch = useAppDispatch();
 const { socket } = useContext(AppContext);

 const server = useSelector(getServerDetails);
 const websiteNumber = useSelector(getDockerWebsiteNumber);

 const [container, setContainers] = useState<Array<IContainerData>>([]);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [listWasEmpty, setListWasEmpty] = useState<boolean>(false);
 const [sitesNumber, setSitesNumber] = useState<number>(0);
 const [reloadData, setReloadData] = useState<boolean>(false);

 useEffect(() => {
  (async () => {
   if (server.id) {
    const containerList = await dispatch(getClusterProxy(server.id, 0, 10));
    if (containerList.dataset.length === 0) setListWasEmpty(true);
    setContainers(containerList.dataset);
    await dispatch(getServerSites(server.id, 0, 10));
    setSitesNumber(websiteNumber);
    setIsLoading(false);
   }
  })();
 }, [server.id]);

 useEffect(() => {
  function onDockerProxy(value: any) {
   if (value?.completed && value?.section === "proxy") {
    setReloadData(true);
   }
  }
  !isNil(socket) && socket.on("docker_data", onDockerProxy);
  return () => {
   !isNil(socket) && socket.off("docker_data", onDockerProxy);
  };
 }, [socket]);

 useEffect(() => {
  (async () => {
   if (reloadData) {
    if (server.id) {
     await dispatch(getServerSites(server.id, 0, 10));
     const containerList = await dispatch(getClusterProxy(server.id || 0, 0, 10));
     setContainers(containerList.dataset);
    }
    setReloadData(false);
   }
  })();
 }, [reloadData]);

 const RenderProxyData = (): ReactElement => {
  let proxy = container.find((element) => element.name.includes("nginx_certbot"));

  if (proxy) {
   return (
    <Stack>
     <TableContainer>
      <Table>
       <TableRow>
        <TableCell component="th" scope="row">
         <Stack>
          <Typography variant="caption">{"nginx_certbot"}</Typography>
          <Typography variant="caption">
           {`Replicas: ${serviceList.find((element) => element.name.includes("nginx_certbot"))
            ?.replicas}`}
          </Typography>
          <Typography variant="caption">Mode: Global</Typography>
         </Stack>
        </TableCell>
        <TableCell component="th" scope="row">
         <Chip size="small" label={proxy.status} color="success" />
        </TableCell>
        <TableCell component="th" scope="row">
         <Stack direction="row" spacing={1}>
          {separatePorts(proxy.ports).map((element, key) => {
           return (
            <Chip
             key={`docker-port-${key}`}
             label={formatPort(element)}
             size="small"
             color="info"
            />
           );
          })}
         </Stack>
        </TableCell>
        <TableCell
         align="right"
         onClick={(e) => {
          e.stopPropagation();
         }}>
         <Stack direction="row" justifyContent="flex-end">
          {sitesNumber === 0 && (
           <DeleteProxy
            id={server.id || 0}
            name={proxy.name}
            proxy_id={proxy.container_id}
            type={proxy.type}
           />
          )}
         </Stack>
        </TableCell>
       </TableRow>
      </Table>
     </TableContainer>
    </Stack>
   );
  } else {
   return <></>;
  }
 };

 return isLoading ? (
  <></>
 ) : container.length > 0 || !listWasEmpty ? (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Stack direction="row" spacing={1}>
     <IconSelector icon="ServiceIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
     <Typography fontSize={16} variant="unboundedTitle">
      <FormattedMessage id="cluster.proxy" />
     </Typography>
    </Stack>
    <Stack direction="row" spacing={2} alignItems="center">
     <ShowProxyInfo />
     {container.find((element) => element.name.includes("nginx_certbot")) ? (
      <></>
     ) : (
      <CreateProxy id={server.id || 0} />
     )}
    </Stack>
   </Stack>
   <RenderProxyData />
  </Paper>
 ) : (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Stack direction="row" spacing={1}>
     <IconSelector icon="ServiceIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
     <Typography fontSize={16} variant="unboundedTitle">
      <FormattedMessage id="cluster.proxy" />
     </Typography>
    </Stack>
   </Stack>
   <Stack alignItems="center">
    <EmptyList showPaper={false} />
    <CreateProxy id={server.id || 0} />
   </Stack>
  </Paper>
 );
};

export default ClusterProxy;
